import { useState, useEffect, useMemo } from 'react';
import { isToday, formatDate, isMultiDayEvent, isAllDayEvent, getEventCardStyle } from '../../utils/calendar';
import { CalendarEvent } from '../../types/calendar';
import { formatTime } from '../../utils/time';
import MemberAvatar from '../MemberAvatar';
import { formatRecurringPattern } from '../../utils/recurrence';
import { getDateTypeIcon } from '../../utils/eventAppearance';
import { Icon } from '../../utils/icons';
import { useTodoStore } from '../../store/todoStore';
import { Todo } from '../../types/todo';
import { getDesignTokens } from '../../utils/designTokens';
import LoadingSpinner from '../LoadingSpinner';
import { useToastStore } from '../../store/toastStore';

interface TodayEventsProps {
  events: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
  isDarkMode?: boolean;
  isEmbedded?: boolean; // When true, removes collapse functionality
}

export default function TodayEvents({
  events,
  onEventClick,
  isDarkMode = false,
  isEmbedded = false,
}: TodayEventsProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { todos, fetchTodos, updateTodo } = useTodoStore();
  const { showToast } = useToastStore();
  const [pendingStatusIds, setPendingStatusIds] = useState<Record<string, boolean>>({});
  const today = new Date();
  const todayEvents = events.filter((event) => isToday(event.startTime));

  // Sort events by start time
  todayEvents.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());

  // Fetch todos
  useEffect(() => {
    fetchTodos().catch((error) => {
      console.error('Error fetching todos:', error);
      showToast(error instanceof Error ? error.message : 'Failed to load todos', 'error');
    });
  }, [fetchTodos, showToast]);

  // Filter todos for today and past due
  const todayTodos = useMemo(() => {
    const dayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
    const dayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
    return todos.filter(todo => {
      // Only show open todos (not done)
      if (todo.status === 'done') return false;
      
      if (!todo.dueDateTime) return false;
      // Parse dueDateTime, handling both Date objects and ISO strings
      // If it's an ISO string, extract date components directly to avoid timezone shifts
      let dueDate: Date;
      if (todo.dueDateTime instanceof Date) {
        dueDate = todo.dueDateTime;
      } else if (typeof todo.dueDateTime === 'string') {
        const dueStr = todo.dueDateTime;
        // Check if it's a date-only string (YYYY-MM-DD) or full ISO string
        if (dueStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          // Date-only string: parse as local date
          const [year, month, day] = dueStr.split('-').map(Number);
          dueDate = new Date(year, month - 1, day);
        } else {
          // Full ISO string: extract date components from UTC to avoid timezone shifts
          const isoDate = new Date(dueStr);
          // Use UTC methods to get the date components, then create local date
          const year = isoDate.getUTCFullYear();
          const month = isoDate.getUTCMonth();
          const day = isoDate.getUTCDate();
          dueDate = new Date(year, month, day);
        }
      } else {
        dueDate = new Date();
      }
      const dueDateStart = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate(), 0, 0, 0);
      
      // Include today's todos and past due todos
      return dueDateStart.getTime() <= dayEnd.getTime();
    }).sort((a, b) => {
      // First, separate past due from today's todos (past due first)
      const dayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
      
      // Parse dueDateTime for comparison
      const parseDueDate = (dueDateTime: Date | string | undefined): Date => {
        if (!dueDateTime) return new Date();
        if (dueDateTime instanceof Date) return dueDateTime;
        if (typeof dueDateTime === 'string') {
          const dueStr = dueDateTime;
          if (dueStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const [year, month, day] = dueStr.split('-').map(Number);
            return new Date(year, month - 1, day);
          } else {
            const isoDate = new Date(dueStr);
            return new Date(isoDate.getUTCFullYear(), isoDate.getUTCMonth(), isoDate.getUTCDate());
          }
        }
        return new Date();
      };
      const aDueDate = parseDueDate(a.dueDateTime);
      const aDueDateStart = new Date(aDueDate.getFullYear(), aDueDate.getMonth(), aDueDate.getDate(), 0, 0, 0);
      const aIsPastDue = aDueDateStart.getTime() < dayStart.getTime();
      
      const bDueDate = parseDueDate(b.dueDateTime);
      const bDueDateStart = new Date(bDueDate.getFullYear(), bDueDate.getMonth(), bDueDate.getDate(), 0, 0, 0);
      const bIsPastDue = bDueDateStart.getTime() < dayStart.getTime();
      
      if (aIsPastDue !== bIsPastDue) {
        return aIsPastDue ? -1 : 1; // Past due first
      }
      
      if (a.status !== b.status) {
        return a.status === 'open' ? -1 : 1;
      }
      const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
      const aPriority = priorityOrder[a.priority || 'medium'];
      const bPriority = priorityOrder[b.priority || 'medium'];
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }
      if (a.dueDateTime && b.dueDateTime) {
        const aTime = a.dueDateTime instanceof Date ? a.dueDateTime : (a.dueDateTime ? new Date(a.dueDateTime) : new Date());
        const bTime = b.dueDateTime instanceof Date ? b.dueDateTime : (b.dueDateTime ? new Date(b.dueDateTime) : new Date());
        return aTime.getTime() - bTime.getTime();
      }
      return 0;
    });
  }, [todos, today]);

  const handleTodoToggle = async (todoId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'open' ? 'done' : 'open';
    setPendingStatusIds((state) => ({ ...state, [todoId]: true }));
    updateTodo(todoId, { status: newStatus })
      .then(() => {
        showToast(newStatus === 'done' ? 'Todo marked done' : 'Todo reopened', 'success');
      })
      .catch((error) => {
        console.error('Error updating todo status:', error);
        showToast(error instanceof Error ? error.message : 'Failed to update todo', 'error');
      })
      .finally(() => {
        setPendingStatusIds((state) => {
          const next = { ...state };
          delete next[todoId];
          return next;
        });
      });
  };

  const renderTodo = (todo: Todo) => {
    // Check if todo is past due
    const isPastDue = (): boolean => {
      if (!todo.dueDateTime || todo.status === 'done') return false;
      const dayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
      // Parse dueDateTime, handling both Date objects and ISO strings
      let dueDate: Date;
      if (todo.dueDateTime instanceof Date) {
        dueDate = todo.dueDateTime;
      } else {
        const dueStr = todo.dueDateTime;
        if (typeof dueStr === 'string' && dueStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = dueStr.split('-').map(Number);
          dueDate = new Date(year, month - 1, day);
        } else {
          const isoDate = new Date(dueStr);
          dueDate = new Date(isoDate.getUTCFullYear(), isoDate.getUTCMonth(), isoDate.getUTCDate());
        }
      }
      const dueDateStart = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate(), 0, 0, 0);
      return dueDateStart.getTime() < dayStart.getTime();
    };

    const pastDue = isPastDue();
    const priorityColors = {
      critical: isDarkMode ? 'bg-red-900/30 border-red-600/50' : 'bg-red-50 border-red-300',
      high: isDarkMode ? 'bg-orange-900/30 border-orange-600/50' : 'bg-orange-50 border-orange-300',
      medium: isDarkMode ? 'bg-blue-900/30 border-blue-600/50' : 'bg-blue-50 border-blue-300',
      low: isDarkMode ? 'bg-gray-800/30 border-gray-600/50' : 'bg-gray-50 border-gray-300',
    };
    const priorityColor = priorityColors[todo.priority || 'medium'];
    const isDone = todo.status === 'done';
    
    // Override colors for past due todos
    const pastDueColors = pastDue 
      ? (isDarkMode ? 'bg-red-900/40 border-red-500/70' : 'bg-red-50 border-red-400')
      : priorityColor;

    return (
      <div
        key={todo.todoId}
        className={`border rounded-landing p-2 sm:p-3 flex items-start gap-2 cursor-pointer touch-manipulation active:scale-[0.98] transition-all ${pastDueColors} ${isDone ? 'opacity-60' : ''} min-h-[44px]`}
        onClick={() => {
          if (!pendingStatusIds[todo.todoId]) {
            handleTodoToggle(todo.todoId, todo.status);
          }
        }}
      >
        <button
          className={`w-6 h-6 rounded-full border flex items-center justify-center flex-shrink-0 mt-0.5 min-h-[24px] min-w-[24px] ${
            isDone ? 'bg-green-500 border-green-600' : 'bg-white border-gray-300'
          }`}
          onClick={(e) => {
            e.stopPropagation();
            handleTodoToggle(todo.todoId, todo.status);
          }}
          aria-label="Toggle status"
          disabled={pendingStatusIds[todo.todoId]}
        >
          {pendingStatusIds[todo.todoId] ? (
            <LoadingSpinner size="sm" label="Updating todo" />
          ) : (
            isDone && <Icon name="check" size="sm" className="text-white" />
          )}
        </button>
        <div className="flex-1 min-w-0">
          <div className={`${isDarkMode ? 'text-gray-100' : 'text-gray-900'} text-sm sm:text-base font-semibold ${isDone ? 'line-through' : ''}`}>
            {todo.title}
          </div>
          {todo.dueDateTime && (
            <div className={`${pastDue ? (isDarkMode ? 'text-red-400' : 'text-red-600') : (isDarkMode ? 'text-gray-400' : 'text-gray-500')} text-xs mt-0.5 font-semibold`}>
              {pastDue ? 'Past due' : formatTime(todo.dueDateTime instanceof Date ? todo.dueDateTime : (todo.dueDateTime ? new Date(todo.dueDateTime) : new Date()))}
            </div>
          )}
          {todo.assigneeName && (
            <div className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} text-xs mt-0.5`}>
              {todo.isFamily ? '👥 Family' : `👤 ${todo.assigneeName}`}
            </div>
          )}
        </div>
        {todo.priority && todo.priority !== 'medium' && (
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold flex-shrink-0 ${
            todo.priority === 'critical' ? 'bg-red-500 text-white' :
            todo.priority === 'high' ? 'bg-orange-500 text-white' :
            'bg-gray-500 text-white'
          }`}>
            {todo.priority}
          </span>
        )}
      </div>
    );
  };

  // Use design tokens instead of hardcoded teal colors
  const tokens = getDesignTokens(isDarkMode);
  const theme = isDarkMode
    ? {
        bg: 'bg-gray-800/90',
        border: tokens.border.light,
        text: tokens.text.primary,
        textMuted: tokens.text.muted,
        header: tokens.text.accent,
        eventBg: `${tokens.button.secondary} ${tokens.border.light}`,
        button: 'bg-gray-700 hover:bg-gray-600 text-gray-200',
      }
    : {
        bg: tokens.card.bg,
        border: tokens.border.light,
        text: tokens.text.primary,
        textMuted: tokens.text.muted,
        header: tokens.text.accent,
        eventBg: `${tokens.button.secondary} ${tokens.border.light}`,
        button: 'bg-gray-100 hover:bg-gray-200 text-gray-700',
      };

  // If embedded, don't show collapse functionality
  if (isEmbedded) {
    return (
      <div className={`${theme.bg} ${theme.border} border-2 rounded-landing shadow-lg h-full flex flex-col`}>
        {/* Header */}
        <div className="flex items-center justify-between p-2 sm:p-2.5">
          <h3 className={`${theme.header} font-bold text-base sm:text-lg lg:text-xl`}>Today's Events</h3>
        </div>
        {/* Content */}
        <div className="flex-1 overflow-y-auto px-2 pb-2">
          <div className={`text-xs sm:text-sm lg:text-base ${theme.textMuted} mb-3 sm:mb-4 font-semibold`}>
            {formatDate(today, 'long')}
          </div>
          <div className="space-y-2 sm:space-y-3">
            {todayEvents.length === 0 && todayTodos.length === 0 ? (
              <div className={`text-xs sm:text-sm lg:text-base ${theme.textMuted} text-center py-4 sm:py-6 font-medium`}>
                No events or todos scheduled for today
              </div>
            ) : (
              <>
                {todayEvents.map((event) => {
                const isMultiDay = isMultiDayEvent(event.startTime, event.endTime);
                const members = event.members && event.members.length > 0
                  ? event.members
                  : (event.memberId ? [{
                      memberId: event.memberId,
                      memberName: event.memberName || '',
                      memberColor: event.memberColor || '#3b82f6',
                      memberAvatar: event.memberAvatar,
                    }] : []);
                const dateTypeIcon = event.isImportantDate ? getDateTypeIcon(event.dateType) : null;
                const recurringPatternText = event.isRecurring ? formatRecurringPattern(event.recurringPattern) : '';
                
                const cardStyle = getEventCardStyle(members, isDarkMode);
                const hasLeftBar = cardStyle.leftBarGradient;
                const leftBarWidth = cardStyle.leftBarWidth ? parseInt(cardStyle.leftBarWidth.replace('px', '')) : 0;
                const paddingLeft = hasLeftBar && leftBarWidth > 0 ? `${leftBarWidth + 8}px` : undefined;
                
                // Ensure border color is consistent - use explicit border color in style to avoid any visual differences
                const defaultBorderColor = isDarkMode ? 'rgba(107, 114, 128, 0.3)' : 'rgba(141, 125, 107, 0.25)'; // Peachy warm tone
                
                return (
                <div
                  key={event.id}
                  className={`border-2 rounded-landing p-2 cursor-pointer transition-all active:scale-95 touch-manipulation min-h-[44px] relative ${isDarkMode ? 'text-gray-100' : 'text-gray-600'}`}
                  style={{
                    backgroundColor: cardStyle.background || (isDarkMode ? 'rgba(31, 41, 55, 1)' : 'rgba(255, 254, 251, 1)'),
                    background: cardStyle.background || (isDarkMode ? 'rgba(31, 41, 55, 1)' : 'rgba(255, 254, 251, 1)'),
                    paddingLeft: paddingLeft,
                    borderColor: defaultBorderColor,
                  }}
                  onClick={() => onEventClick?.(event)}
                >
                  {/* Left bar gradient overlay for multi-member events */}
                  {hasLeftBar && (
                    <div
                      className="absolute left-0 top-0 bottom-0"
                      style={{
                        width: cardStyle.leftBarWidth || '4px',
                        background: cardStyle.leftBarGradient,
                        borderTopLeftRadius: '18px', // Match rounded-landing (18px)
                        borderBottomLeftRadius: '18px', // Match rounded-landing (18px)
                        zIndex: 1,
                      }}
                    />
                  )}
                  {/* Multi-day indicator: Top-left corner triangle */}
                  {isMultiDay && (
                    <div
                      className="absolute top-0 left-0 pointer-events-none"
                      style={{
                        width: 0,
                        height: 0,
                        borderTop: isDarkMode 
                          ? '20px solid rgba(251, 191, 36, 0.3)' // Subtle amber for dark mode
                          : '20px solid rgba(251, 191, 36, 0.25)', // Subtle amber for light mode
                        borderRight: '20px solid transparent',
                        zIndex: 10,
                      }}
                    />
                  )}
                  {/* Event content */}
                  <div className="mb-2 sm:mb-2">
                    <div className="flex items-center gap-2 mb-1 sm:mb-1">
                      {dateTypeIcon && (
                        <span title={event.dateType || undefined}>
                          <Icon name={dateTypeIcon} size="md" className={isDarkMode ? 'text-gray-100' : 'text-gray-600'} />
                        </span>
                      )}
                      {event.isRecurring && recurringPatternText && (
                        <span className="text-xs sm:text-sm opacity-90" title={recurringPatternText}>
                          <span className="opacity-75">↻</span>
                        </span>
                      )}
                      <div className={`font-medium text-sm sm:text-base lg:text-lg ${isDarkMode ? 'text-gray-100' : 'text-gray-600'}`}>
                        {event.title}
                      </div>
                    </div>
                    <div className={`text-xs sm:text-sm lg:text-base ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} mb-1 sm:mb-1 font-normal`}>
                      {formatTime(event.startTime)}
                      {event.endTime &&
                        ` - ${formatTime(event.endTime)}`}
                    </div>
                    {event.location && (
                      <div className={`text-[10px] sm:text-xs lg:text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} font-normal`}>
                        📍 {event.location}
                      </div>
                    )}
                  </div>
                  
                  {/* Member Avatars - Row at bottom */}
                  {(() => {
                    // Support both legacy (single member) and new (multiple members) format
                    const members = event.members && event.members.length > 0
                      ? event.members
                      : (event.memberId ? [{
                          memberId: event.memberId,
                          memberName: event.memberName || '',
                          memberColor: event.memberColor || '#3b82f6',
                          memberAvatar: event.memberAvatar,
                        }] : []);
                    
                    if (members.length === 0) return null;
                    
                    return (
                      <div className="flex items-center gap-1.5 sm:gap-2 pt-2 border-t border-opacity-20" style={{ borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}>
                        {members.map((member, idx) => (
                          <MemberAvatar
                            key={member.memberId || idx}
                            member={member}
                            size="lg"
                          />
                        ))}
                      </div>
                    );
                  })()}
                </div>
                );
              })}
              
              {/* Todos Section */}
              {todayTodos.length > 0 && (
                <div className={todayEvents.length > 0 ? 'mt-4 pt-4 border-t' : ''} style={{ borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}>
                  <h4 className={`${theme.header} font-bold text-sm sm:text-base mb-2 sm:mb-3`}>To Dos</h4>
                  <div className="space-y-2">
                    {todayTodos.map(renderTodo)}
                  </div>
                </div>
              )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Original collapsible version (for backward compatibility)
  return (
    <div className="relative h-full flex">
      {/* Collapsed Tab - Shows when collapsed, positioned on the left edge */}
      {isCollapsed && (
        <button
          onClick={() => setIsCollapsed(false)}
          className={`absolute left-0 top-1/2 -translate-y-1/2 ${theme.button} ${theme.border} border-r-2 rounded-sm-l-lg px-2 py-6 shadow-lg z-20 min-w-[36px] min-h-[100px] flex items-center justify-center transition-all active:scale-95`}
          style={{ transform: 'translateY(-50%)' }}
          aria-label="Expand Today's Events"
        >
          <span className="text-xs sm:text-sm font-semibold transform -rotate-90 whitespace-nowrap">Today</span>
        </button>
      )}

      {/* Main Content - Slides in/out */}
      <div
        className={`${theme.bg} ${theme.border} border-2 rounded-landing shadow-lg h-full flex flex-col transition-all duration-300 ease-in-out ${
          isCollapsed ? 'translate-x-full opacity-0 w-0 overflow-hidden' : 'translate-x-0 opacity-100 w-full'
        }`}
      >
        {/* Header with Collapse Button */}
        <div className="flex items-center justify-between p-2 sm:p-2.5">
          <h3 className={`${theme.header} font-bold text-base sm:text-lg lg:text-xl`}>Today's Events</h3>
          <button
            onClick={() => setIsCollapsed(true)}
            className={`${theme.button} ${theme.border} border rounded-sm p-2 min-w-[44px] min-h-[44px] flex items-center justify-center transition-all active:scale-95`}
            aria-label="Collapse Today's Events"
          >
            <span className="text-base sm:text-lg">→</span>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-2 pb-2">
          <div className={`text-xs sm:text-sm lg:text-base ${theme.textMuted} mb-3 sm:mb-4 font-semibold`}>
            {formatDate(today, 'long')}
          </div>
          <div className="space-y-2 sm:space-y-3">
        {todayEvents.length === 0 && todayTodos.length === 0 ? (
          <div className={`text-xs sm:text-sm lg:text-base ${theme.textMuted} text-center py-4 sm:py-6 font-medium`}>
            No events or todos scheduled for today
          </div>
        ) : (
          <>
          {todayEvents.map((event) => {
            const isMultiDay = isMultiDayEvent(event.startTime, event.endTime);
            const members = event.members && event.members.length > 0
              ? event.members
              : (event.memberId ? [{
                  memberId: event.memberId,
                  memberName: event.memberName || '',
                  memberColor: event.memberColor || '#3b82f6',
                  memberAvatar: event.memberAvatar,
                }] : []);
                const dateTypeIcon = event.isImportantDate ? getDateTypeIcon(event.dateType) : null;
                const recurringPatternText = event.isRecurring ? formatRecurringPattern(event.recurringPattern) : '';
            
            const cardStyle2 = getEventCardStyle(members, isDarkMode);
            const hasLeftBar2 = cardStyle2.leftBarGradient;
            const leftBarWidth2 = cardStyle2.leftBarWidth ? parseInt(cardStyle2.leftBarWidth.replace('px', '')) : 0;
            const paddingLeft2 = hasLeftBar2 && leftBarWidth2 > 0 ? `${leftBarWidth2 + 8}px` : undefined;
            
            return (
            <div
              key={event.id}
              className={`${theme.border} border-2 rounded-sm p-2 cursor-pointer transition-all active:scale-95 touch-manipulation min-h-[44px] relative ${isDarkMode ? 'text-gray-100' : 'text-gray-600'}`}
              style={{
                backgroundColor: cardStyle2.background || (isDarkMode ? 'rgba(31, 41, 55, 1)' : 'rgba(255, 254, 251, 1)'),
                background: cardStyle2.background || (isDarkMode ? 'rgba(31, 41, 55, 1)' : 'rgba(255, 254, 251, 1)'),
                paddingLeft: paddingLeft2,
              }}
              onClick={() => onEventClick?.(event)}
            >
              {/* Left bar gradient overlay for multi-member events */}
              {hasLeftBar2 && (
                <div
                  className="absolute left-0 top-0 bottom-0"
                  style={{
                    width: cardStyle2.leftBarWidth || '4px',
                    background: cardStyle2.leftBarGradient,
                    borderTopLeftRadius: '18px', // Match rounded-landing (18px)
                    borderBottomLeftRadius: '18px', // Match rounded-landing (18px)
                    zIndex: 1,
                  }}
                />
              )}
              {/* Multi-day indicator: Top-left corner triangle */}
              {isMultiDay && (
                <div
                  className="absolute top-0 left-0 pointer-events-none"
                  style={{
                    width: 0,
                    height: 0,
                    borderTop: '20px solid #fbbf24', // Amber color
                    borderRight: '20px solid transparent',
                    zIndex: 10,
                  }}
                />
              )}
              {/* Event content */}
              <div className="mb-2 sm:mb-2">
                <div className="flex items-center gap-2 mb-1 sm:mb-1">
                  {dateTypeIcon && (
                    <span className="text-sm sm:text-base" title={event.dateType}>
                      {dateTypeIcon}
                    </span>
                  )}
                  {event.isRecurring && recurringPatternText && (
                    <span className="text-xs sm:text-sm opacity-75" title={recurringPatternText}>
                      ↻
                    </span>
                  )}
                  <div className={`font-medium text-sm sm:text-base lg:text-lg ${isDarkMode ? 'text-gray-100' : 'text-gray-600'}`}>
                    {event.title}
                  </div>
                </div>
                {(() => {
                  const eventStart = event.startTime instanceof Date ? event.startTime : new Date(event.startTime);
                  const eventEnd = event.endTime instanceof Date ? event.endTime : new Date(event.endTime);
                  const isAllDay = isAllDayEvent(eventStart, eventEnd, event.isAllDay);
                  
                  if (isAllDay) return null;
                  
                  return (
                    <div className={`text-xs sm:text-sm lg:text-base ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} mb-1 sm:mb-1 font-normal`}>
                      {formatTime(eventStart)}
                      {eventEnd &&
                        ` - ${formatTime(eventEnd)}`}
                    </div>
                  );
                })()}
                {event.location && (
                  <div className={`text-[10px] sm:text-xs lg:text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} font-normal`}>
                    📍 {event.location}
                  </div>
                )}
              </div>
              
              {/* Member Avatars - Row at bottom */}
              {(() => {
                // Support both legacy (single member) and new (multiple members) format
                const members = event.members && event.members.length > 0
                  ? event.members
                  : (event.memberId ? [{
                      memberId: event.memberId,
                      memberName: event.memberName || '',
                      memberColor: event.memberColor || '#3b82f6',
                      memberAvatar: event.memberAvatar,
                    }] : []);
                
                if (members.length === 0) return null;
                
                return (
                  <div className="flex items-center gap-1.5 sm:gap-2 pt-2 border-t border-opacity-20" style={{ borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}>
                    {members.map((member, idx) => (
                      <MemberAvatar
                        key={member.memberId || idx}
                        member={member}
                        size="lg"
                      />
                    ))}
                  </div>
                );
              })()}
            </div>
            );
          })}
          
          {/* Todos Section */}
          {todayTodos.length > 0 && (
            <div className={todayEvents.length > 0 ? 'mt-4 pt-4 border-t' : ''} style={{ borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}>
              <h4 className={`${theme.header} font-bold text-sm sm:text-base mb-2 sm:mb-3`}>To Dos</h4>
              <div className="space-y-2">
                {todayTodos.map(renderTodo)}
              </div>
            </div>
          )}
          </>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}

